import { atom } from 'jotai'
import type { Getter } from 'jotai'
import { mapOmanFormatData } from '../utils/oman-format.mapper'
import { findProductByFlavorOrItemCode, getShipmentPacks, isKnownCartItemCode, isRecord } from '../utils/oman-shipment-packs.helper'
import { cartAtom, cartEditedAtom, v2CartAtom } from '@/core/cart/cart.store'
import { fgProductsAtom } from '@/core/feelgreat/feelgreat.store'
import type {
    TOmanRegion,
    TOmanCountryItem,
    TOmanShippingMethodId,
    TOmanPaymentMethod,
    TOmanCheckoutForm,
    TOmanShippingForm,
    TOmanCreditCardForm,
    TOmanFormatData,
    TOmanCreditcardAlias,
    ProcessingPaymentState,
    PaymentsV3DuplicateOrderResponse
} from '../types/oman.types'
import type { FGProducts } from '@/core/feelgreat/feelgreat.types'

// ─── Geo data ──────────────────────────────────────────────────────────────────
export const omanRegionsAtom = atom<TOmanRegion[]>([])
export const omanCountryListAtom = atom<TOmanCountryItem[]>([])
export const isLoadingOmanGeoAtom = atom<boolean>(false)

export const initialOmanShippingMethod: TOmanShippingMethodId = 'delivery'
export const initialOmanPaymentMethod: TOmanPaymentMethod = 'creditcard'
export const initialOmanMemberShippingForm: TOmanShippingForm = {
    fullname: '',
    email: '',
    phone: '',
    country: 'OM',
    address1: '',
    address2: '',
    city: '',
    state: ''
}

// ─── Shipping method ───────────────────────────────────────────────────────────
export const omanSelectedShippingMethodAtom = atom<TOmanShippingMethodId>(initialOmanShippingMethod)

// ─── Payment method ────────────────────────────────────────────────────────────
export const omanSelectedPaymentMethodAtom = atom<TOmanPaymentMethod>(initialOmanPaymentMethod)

// ─── Form state ────────────────────────────────────────────────────────────────
export const omanCheckoutFormAtom = atom<TOmanCheckoutForm | null>(null)
export const omanMemberShippingFormAtom = atom<TOmanShippingForm>(initialOmanMemberShippingForm)
export const omanCreditCardFormAtom = atom<TOmanCreditCardForm | null>(null)

// ─── Saved cards ───────────────────────────────────────────────────────────────
export const omanSavedCardsAtom = atom<TOmanCreditcardAlias[]>([])

// ─── Format data (loaded from getHotFormatV2) ─────────────────────────────────
// Raw API response stored as-is; mapped to TOmanFormatData at read-time.
//
// Frontend-authoritative item list (mirrors legacy `CalculateCart`)
// ----------------------------------------------------------------
// Hydra's `marketing.items[]` does not expose a per-line subscription flag,
// and — critically — in Oman the same flavour often reuses one `item_code`
// across `onetime` and `subscribe` purchase types. That means any overlay
// keyed on `item_code` is ambiguous: if the user has the same flavour in
// both sub-carts, the later iteration always wins and one of the lines is
// mislabelled.
//
// The legacy U-Shop stack (see `ushop-feelgreat-2/document.md` §2-4) avoids
// this by *constructing* the line items directly from the cart — keyed by
// `(purchaseType, flavor)` — and layering pricing/PV from the Hydra
// response. We replicate that here: the cart owns `type` and `qty`, the
// backend owns pricing. Items present in the Hydra response that do not
// originate from the cart (e.g. freebies, bonus kits) are preserved so the
// rendered totals still match what the server calculated.
const buildItemsFromCart = (get: Getter, mapped: TOmanFormatData, raw: Record<string, unknown>): TOmanFormatData['items'] => {
    const cart = get(cartAtom)
    const v2Cart = get(v2CartAtom)
    const fgProducts = get(fgProductsAtom)
    const cartEdited = get(cartEditedAtom)

    const hasCart = v2Cart.length > 0 || Object.values(cart).some(flavors => Object.values(flavors).some(qty => qty > 0))
    // Empty cart is ambiguous: it can mean "not hydrated yet" (initial
    // visit / read-only page) or "the user just cleared it". The
    // `cartEditedAtom` signal disambiguates — once the user has touched
    // the cart, cart is authoritative and an empty cart should render as
    // an empty summary rather than falling back to the last server items.
    if (!hasCart) {
        if (cartEdited) return []
        return buildItemsFromShipmentPacks(raw, mapped, fgProducts) ?? mapped.items
    }

    const hydraByCode = new Map<string, TOmanFormatData['items'] extends (infer T)[] | undefined ? T : never>()
    for (const it of mapped.items ?? []) {
        if (it.item_code) hydraByCode.set(it.item_code, it)
    }

    const consumed = new Set<string>()
    const result: NonNullable<TOmanFormatData['items']> = []

    for (const [purchaseType, flavors] of Object.entries(cart)) {
        for (const [flavor, qty] of Object.entries(flavors)) {
            if (!qty || qty <= 0) continue
            const product = fgProducts?.[flavor]?.[purchaseType]
            if (!product) continue
            const code = product.item_code
            const hydra = code ? hydraByCode.get(code) : undefined
            if (code && hydra) consumed.add(code)

            const unitPrice = hydra?.price ?? product.price?.member ?? product.price?.retail ?? 0

            result.push({
                item_code: code,
                name: hydra?.name || product.flavor?.titleInOrderSummary?.english || product.flavor?.text?.english || '',
                qty,
                price: unitPrice,
                pv: hydra?.pv ?? product.pv,
                type: purchaseType === 'subscribe' ? 'subscription' : 'onetime'
            })
        }
    }

    // Keep server-added lines (e.g. freebies) that the cart never produced.
    // Once the user edits the cart, stale Hydra lines for regular Feel Great
    // products must not be preserved while the debounced PUT/GET is pending.
    for (const it of mapped.items ?? []) {
        if (cartEdited && it.item_code && isKnownCartItemCode(fgProducts, it.item_code)) continue
        if (!it.item_code || !consumed.has(it.item_code)) result.push(it)
    }

    return result
}

const buildItemsFromShipmentPacks = (raw: Record<string, unknown>, mapped: TOmanFormatData, fgProducts: FGProducts): TOmanFormatData['items'] | null => {
    const packs = getShipmentPacks(raw)
    if (!isRecord(packs)) return null

    const hydraByCode = new Map<string, NonNullable<TOmanFormatData['items']>[number]>()
    for (const item of mapped.items ?? []) {
        if (item.item_code) hydraByCode.set(item.item_code, item)
    }

    const result: NonNullable<TOmanFormatData['items']> = []
    const buckets: Array<[string, 'subscription' | 'onetime']> = [
        ['subscribe', 'subscription'],
        ['subscription', 'subscription'],
        ['onetime', 'onetime'],
        ['oneTime', 'onetime']
    ]

    for (const [purchaseType, type] of buckets) {
        const bucket = packs[purchaseType]
        if (!isRecord(bucket)) continue

        for (const [key, qtyValue] of Object.entries(bucket)) {
            const qty = Number(qtyValue)
            if (!qty || qty <= 0) continue

            const match = findProductByFlavorOrItemCode(fgProducts, purchaseType === 'subscription' ? 'subscribe' : purchaseType, key)
            const code = match?.product.item_code ?? key
            const hydra = hydraByCode.get(code)
            result.push({
                item_code: code,
                name: hydra?.name || match?.product.flavor?.titleInOrderSummary?.english || match?.product.flavor?.text?.english || '',
                qty,
                price: hydra?.price ?? match?.product.price?.member ?? match?.product.price?.retail ?? 0,
                pv: hydra?.pv ?? match?.product.pv,
                type
            })
        }
    }

    return result.length > 0 ? result : null
}

export const omanFormatRawAtom = atom<Record<string, unknown> | null>(null)
export const omanFormatDataAtom = atom(
    (get): TOmanFormatData | null => {
        const raw = get(omanFormatRawAtom)
        if (!raw) return null
        const mapped = mapOmanFormatData(raw)

        const rebuiltItems = buildItemsFromCart(get, mapped, raw)
        const hasSubscription = rebuiltItems !== undefined ? rebuiltItems.some(it => it.type === 'subscription') : Boolean(mapped.hasSubscription)
        return { ...mapped, items: rebuiltItems, hasSubscription }
    },
    (_get, set, raw: Record<string, unknown> | null) => {
        set(omanFormatRawAtom, raw)
    }
)
export const isLoadingOmanFormatAtom = atom<boolean>(false)

// Stores the freight amount from the last "delivery" format response.
// When switching to pickup, the API returns freight=0 but we keep the
// delivery freight so the shipping method selector can still display the price.
export const omanDeliveryFreightAtom = atom<number>(0)
export const omanDeliveryFreightSyncedUuidAtom = atom<string | null>(null)

// ─── Checkout loading / errors (shared with default store keys for convenience) ─
export const omanCheckoutLoadingAtom = atom<boolean>(false)
export const omanCheckoutErrorsAtom = atom<string[]>([])

// ─── Derived: cart has any subscription item ──────────────────────────────────
export const omanCartHasSubscriptionAtom = atom<boolean>(get => {
    const format = get(omanFormatDataAtom)
    return format?.hasSubscription ?? false
})

// ─── Derived: available payment methods filtered by subscription ──────────────
export const omanAvailablePaymentMethodsAtom = atom<TOmanPaymentMethod[]>(get => {
    const shippingMethod = get(omanSelectedShippingMethodAtom)
    const hasSubscription = get(omanCartHasSubscriptionAtom)

    if (shippingMethod === 'delivery') {
        const all: TOmanPaymentMethod[] = ['creditcard', 'bankwire', 'cashOnDelivery']
        return hasSubscription ? ['creditcard', 'creditCardAlias'] : all
    } else {
        const all: TOmanPaymentMethod[] = ['creditcard', 'bankwire', 'pickupPayment']
        return hasSubscription ? ['creditcard', 'creditCardAlias'] : all
    }
})

// ─── PaymentsV3 processing state ────────────────────────────────────────────
// Initial step values mirror what `paymentsV3/status` would return on its
// first tick — so the modal can render real spinners while createPaymentV3()
// is in-flight (before any poll response exists).
export const initialOmanProcessingPayment: ProcessingPaymentState = {
    status: 'idle',
    hydra_status: 'processing',
    payment_status: 'waiting'
}
export const omanProcessingPaymentAtom = atom<ProcessingPaymentState>(initialOmanProcessingPayment)
export const omanProcessingPaymentModalOpenAtom = atom<boolean>(false)

// ─── Cart qty syncing (backdrop while PUT+GET in progress) ─────────────────
export const omanCartSyncingAtom = atom<boolean>(false)

// ─── Duplicate order dialog state ──────────────────────────────────────────
export const omanDuplicateOrderAtom = atom<PaymentsV3DuplicateOrderResponse | null>(null)
